import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Coffee, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

export default function RegisterPage() {
	const { signUp } = useAuth();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [acceptNewsletter, setAcceptNewsletter] = useState(true);
	const [authError, setAuthError] = useState("");
	const [emailModalOpen, setEmailModalOpen] = useState(false);

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		phone: "",
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCloseModal = () => {
		setEmailModalOpen(false);
		navigate("/verification-email", {
			state: { email: formData.email.trim().toLowerCase() },
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setAuthError("");
		setEmailModalOpen(false);

		const passwordErrors = [];
		if (formData.password.length < 8)
			passwordErrors.push("au moins 8 caractères");
		if (!/[a-z]/.test(formData.password)) passwordErrors.push("une minuscule");
		if (!/[A-Z]/.test(formData.password)) passwordErrors.push("une majuscule");
		if (!/[0-9]/.test(formData.password)) passwordErrors.push("un chiffre");
		if (!/[^a-zA-Z0-9]/.test(formData.password))
			passwordErrors.push("un symbole (!@#$%...)");

		if (passwordErrors.length > 0) {
			setAuthError(
				`Le mot de passe doit contenir : ${passwordErrors.join(", ")}.`,
			);
			return;
		}

		setIsLoading(true);

		try {
			const result = await signUp({
				first_name: formData.firstName.trim(),
				last_name: formData.lastName.trim(),
				email: formData.email.trim().toLowerCase(),
				password: formData.password,
				phone: formData.phone.trim(),
				newsletter: acceptNewsletter,
			});

			if (!result.success) {
				setAuthError(result.error || "Erreur lors de l'inscription");
				return;
			}
			// Dans handleSubmit, après result.success
			if (acceptNewsletter && !result.emailConfirmationRequired) {
				await supabase.functions.invoke("confirm-newsletter-subscription", {
					body: {
						email: formData.email.trim().toLowerCase(),
						first_name: formData.firstName.trim() || undefined,
					},
				});
			}

			if (result.emailConfirmationRequired) {
				setEmailModalOpen(true);
				return;
			}

			toast.success("Compte créé avec succès !");
			navigate("/connexion");
		} catch (error) {
			console.error("Erreur inscription :", error);
			setAuthError("Une erreur est survenue lors de l'inscription");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<main
				data-testid="register-page"
				className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16"
			>
				<div className="w-full max-w-lg px-6">
					<div className="bg-white p-8 lg:p-10 border border-border-subtle">
						<div className="text-center mb-8">
							<Link
								to="/"
								className="inline-flex items-center justify-center mb-6"
							>
								<div className="w-14 h-14 rounded-full bg-latte-gold/10 flex items-center justify-center mr-3">
									<Coffee className="w-7 h-7 text-latte-gold" />
								</div>
								<span className="font-serif text-2xl font-semibold text-espresso">
									Léger&apos;s <span className="text-latte-gold">Coffee</span>
								</span>
							</Link>

							<h1 className="font-serif text-2xl lg:text-3xl text-espresso font-semibold mb-2">
								Créer un Compte
							</h1>

							<p className="text-soft-charcoal-secondary">
								Rejoignez la communauté Léger&apos;s Coffee
							</p>
						</div>

						<div className="bg-warm-secondary p-4 mb-8 space-y-2">
							<p className="text-sm font-medium text-espresso mb-2">
								Avantages membres :
							</p>

							<div className="flex items-center text-sm text-soft-charcoal-secondary">
								<Check className="w-4 h-4 text-latte-gold mr-2 flex-shrink-0" />
								<span>Historique de vos commandes</span>
							</div>

							<div className="flex items-center text-sm text-soft-charcoal-secondary">
								<Check className="w-4 h-4 text-latte-gold mr-2 flex-shrink-0" />
								<span>Offres exclusives et réductions</span>
							</div>

							<div className="flex items-center text-sm text-soft-charcoal-secondary">
								<Check className="w-4 h-4 text-latte-gold mr-2 flex-shrink-0" />
								<span>Commande rapide avec vos infos sauvegardées</span>
							</div>
						</div>

						<form onSubmit={handleSubmit} className="space-y-5">
							<div className="grid grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label
										htmlFor="firstName"
										className="text-soft-charcoal font-medium"
									>
										Prénom *
									</Label>
									<Input
										id="firstName"
										name="firstName"
										type="text"
										value={formData.firstName}
										onChange={handleInputChange}
										required
										className="input-field h-12"
										data-testid="register-firstname"
									/>
								</div>

								<div className="space-y-2">
									<Label
										htmlFor="lastName"
										className="text-soft-charcoal font-medium"
									>
										Nom *
									</Label>
									<Input
										id="lastName"
										name="lastName"
										type="text"
										value={formData.lastName}
										onChange={handleInputChange}
										required
										className="input-field h-12"
										data-testid="register-lastname"
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="email"
									className="text-soft-charcoal font-medium"
								>
									Email *
								</Label>
								<Input
									id="email"
									name="email"
									type="email"
									value={formData.email}
									onChange={handleInputChange}
									required
									className="input-field h-12"
									placeholder="votre@email.com"
									data-testid="register-email"
								/>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="phone"
									className="text-soft-charcoal font-medium"
								>
									Téléphone (optionnel)
								</Label>
								<Input
									id="phone"
									name="phone"
									type="tel"
									value={formData.phone}
									onChange={handleInputChange}
									className="input-field h-12"
									placeholder="+41 22 123 45 67"
									data-testid="register-phone"
								/>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="password"
									className="text-soft-charcoal font-medium"
								>
									Mot de passe *
								</Label>

								<div className="relative">
									<Input
										id="password"
										name="password"
										type={showPassword ? "text" : "password"}
										value={formData.password}
										onChange={handleInputChange}
										required
										minLength={8}
										className="input-field h-12 pr-12"
										placeholder="Min. 8 car. avec majuscule, chiffre et symbole"
										data-testid="register-password"
									/>

									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-4 top-1/2 -translate-y-1/2 text-soft-charcoal-secondary hover:text-espresso"
										aria-label={
											showPassword
												? "Masquer le mot de passe"
												: "Afficher le mot de passe"
										}
									>
										{showPassword ? (
											<EyeOff className="w-5 h-5" />
										) : (
											<Eye className="w-5 h-5" />
										)}
									</button>
								</div>
								{/* Après le champ password, avant la fermeture du div */}
								{formData.password && (
									<div
										style={{
											display: "flex",
											gap: 6,
											marginTop: 6,
											flexWrap: "wrap",
										}}
									>
										{[
											{ label: "8 car.", ok: formData.password.length >= 8 },
											{
												label: "Minuscule",
												ok: /[a-z]/.test(formData.password),
											},
											{
												label: "Majuscule",
												ok: /[A-Z]/.test(formData.password),
											},
											{ label: "Chiffre", ok: /[0-9]/.test(formData.password) },
											{
												label: "Symbole",
												ok: /[^a-zA-Z0-9]/.test(formData.password),
											},
										].map((rule) => (
											<span
												key={rule.label}
												style={{
													fontSize: 11,
													padding: "2px 8px",
													borderRadius: 20,
													background: rule.ok ? "#EAF3DE" : "#F5F3EE",
													color: rule.ok ? "#3B6D11" : "#8A857D",
													fontWeight: 500,
												}}
											>
												{rule.ok ? "✓" : "·"} {rule.label}
											</span>
										))}
									</div>
								)}
							</div>

							<div className="flex items-start space-x-3 pt-2">
								<Checkbox
									id="newsletter"
									checked={acceptNewsletter}
									onCheckedChange={(checked) => setAcceptNewsletter(!!checked)}
									className="mt-0.5"
								/>
								<Label
									htmlFor="newsletter"
									className="text-sm text-soft-charcoal-secondary cursor-pointer leading-relaxed"
								>
									Je souhaite recevoir les actualités, offres exclusives et
									nouveautés de Léger&apos;s Coffee par email.
								</Label>
							</div>

							{authError && (
								<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
									{authError}
								</div>
							)}

							<Button
								type="submit"
								disabled={isLoading}
								className="w-full btn-primary h-14 text-base"
								data-testid="register-submit"
							>
								{isLoading ? "Création..." : "Créer mon Compte"}
							</Button>
						</form>

						<div className="mt-8 pt-8 border-t border-border-subtle text-center">
							<p className="text-soft-charcoal-secondary mb-3">
								Déjà un compte ?
							</p>

							<Link
								to="/connexion"
								className="inline-flex items-center text-espresso font-medium hover:text-latte-gold transition-colors duration-300"
								data-testid="login-link"
							>
								Se connecter
								<ArrowRight className="w-4 h-4 ml-2" />
							</Link>
						</div>
					</div>
				</div>
			</main>

			<Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Confirmez votre adresse email</DialogTitle>
						<DialogDescription>
							Votre compte a bien été créé. Vous devez maintenant valider votre
							adresse email avant de pouvoir vous connecter. Vérifiez votre
							boîte mail puis cliquez sur le lien de confirmation.
						</DialogDescription>
					</DialogHeader>

					<DialogFooter>
						<Button onClick={handleCloseModal}>Continuer</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
